import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { PexIcon } from "../../../components/icons/PexIcon";
import type { SellerStoresStrings } from "../../../i18n/seller/stores";
import type { StoreProviderDefinition } from "./store-providers";

export type ConnectedStoreSummary = {
  id: string;
  shopName: string;
  provider: StoreProviderDefinition;
  active: boolean;
  connected: boolean;
  receivingOrders: boolean;
};

export function ConnectedStoreCard({
  store,
  copy,
  onListProduct,
  onViewStore,
  onEditStore,
  onReconnect,
  onDisconnect,
  disconnectPending = false,
  lastSyncAt,
  orderIntakePending = false,
  orderIntakeArming = false,
  detailsAvailable = true,
  detailsExpanded,
  onToggleDetails,
  onToggleOrderIntake,
  children,
}: {
  store: ConnectedStoreSummary;
  copy: SellerStoresStrings;
  onListProduct: () => void;
  onViewStore: () => void;
  onEditStore: () => void;
  //: Re-run this store's OAuth (re-consent). Omit to hide the menu item.
  onReconnect?: () => void;
  //: Soft-delete this store integration. Omit to hide the menu item.
  onDisconnect?: () => void;
  disconnectPending?: boolean;
  lastSyncAt?: string | null;
  orderIntakePending?: boolean;
  //: preflight open, awaiting Confirm — the switch shows a half-set amber
  //: state so the pause reads as "pending your confirmation", not a dead click.
  orderIntakeArming?: boolean;
  detailsAvailable?: boolean;
  detailsExpanded: boolean;
  onToggleDetails: () => void;
  onToggleOrderIntake: () => void;
  children?: ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  // The menu is rendered in a portal on document.body so it can never be
  // clipped by the group's `overflow-hidden` nor painted behind the next
  // store card — the bug where the ⋯ popup slid under the row below it.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null,
  );
  const menuId = useId();
  const detailsId = useId();
  const menuAreaRef = useRef<HTMLDivElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const MENU_WIDTH = 224; // w-56

  const closeMenu = () => {
    setMenuOpen(false);
    setConfirmDisconnect(false);
  };

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPos({
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - MENU_WIDTH),
      });
    }
    setConfirmDisconnect(false);
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) return;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        menuAreaRef.current?.contains(target) ||
        menuPanelRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      closeMenu();
      triggerRef.current?.focus();
    };
    // A fixed-positioned portal detaches from the trigger on scroll/resize —
    // close rather than let it float out of place.
    const closeOnReflow = () => closeMenu();

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeOnReflow, true);
    window.addEventListener("resize", closeOnReflow);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeOnReflow, true);
      window.removeEventListener("resize", closeOnReflow);
    };
  }, [menuOpen]);

  const runMenuAction = (action: () => void) => {
    closeMenu();
    action();
  };

  const toggleFromSummary = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('button, a, input, select, textarea, [role="menuitem"]')
    ) {
      return;
    }
    if (detailsAvailable) onToggleDetails();
  };

  const toggleFromKeyboard = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onToggleDetails();
  };

  const lastSync = lastSyncAt ? new Date(lastSyncAt) : null;
  const connectionDetail =
    lastSync && !Number.isNaN(lastSync.getTime())
      ? copy.group.lastSync(
          new Intl.DateTimeFormat(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }).format(lastSync),
        )
      : store.connected
        ? copy.group.authorizationActive
        : copy.group.authorizationRequired;

  // One status per row: the most urgent problem wins, so the row never shows
  // overlapping Active / Connected / Receiving chips.
  const statusBadge: {
    variant: "success" | "warning" | "neutral";
    label: string;
  } = !store.active
    ? { variant: "neutral", label: copy.status.inactive }
    : !store.connected
      ? { variant: "warning", label: copy.status.notConnected }
      : store.receivingOrders
        ? { variant: "success", label: copy.status.receivingOrders }
        : { variant: "warning", label: copy.status.notReceivingOrders };

  return (
    <article
      aria-label={copy.group.storeLabel(store.shopName)}
      className="bg-[var(--color-surface)]"
    >
      <div
        onClick={toggleFromSummary}
        className="relative grid cursor-pointer gap-3 px-3.5 py-3 sm:px-4 lg:grid-cols-[minmax(160px,1.15fr)_minmax(168px,1fr)_auto] lg:items-center"
      >
        {detailsAvailable ? (
          <button
            type="button"
            aria-label={
              detailsExpanded
                ? copy.actions.hideDetails(store.shopName)
                : copy.actions.showDetails(store.shopName)
            }
            aria-expanded={detailsExpanded}
            aria-controls={detailsId}
            onClick={onToggleDetails}
            onKeyDown={toggleFromKeyboard}
            className="absolute inset-0 z-0 rounded-lg transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-signal)] active:bg-[var(--color-surface-muted)]"
          />
        ) : null}

        <div className="pointer-events-none relative z-10 min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3
              data-testid="connected-store-name"
              className="truncate text-sm font-semibold text-[var(--color-text)]"
            >
              {store.shopName}
            </h3>
            <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-[var(--color-text-muted)]">
            {store.provider.name}
          </p>
        </div>

        <div className="pointer-events-none relative z-10 min-w-0">
          <p className="truncate text-[11px] text-[var(--color-text-muted)]">
            {orderIntakePending ? copy.group.updating : connectionDetail}
          </p>
          {store.connected && !store.receivingOrders ? (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              {copy.group.actionRecommended}
            </p>
          ) : null}
        </div>

        <div className="relative z-20 flex flex-wrap items-center gap-1.5 lg:justify-end">
          <Button variant="primary" size="sm" onClick={onListProduct}>
            {copy.actions.listProduct}
          </Button>
          <Button variant="outline" size="sm" onClick={onViewStore}>
            {copy.actions.manage}
          </Button>
          {detailsAvailable ? (
            <span
              aria-hidden="true"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center sm:min-h-[36px] sm:min-w-[36px]"
            >
              <PexIcon
                name="caretDown"
                size="xs"
                tone="muted"
                className={`transition-transform motion-reduce:transition-none ${
                  detailsExpanded ? "rotate-180" : ""
                }`}
              />
            </span>
          ) : null}
          <div ref={menuAreaRef} className="relative">
            <Button
              ref={triggerRef}
              variant="ghost"
              size="icon"
              aria-label={copy.actions.moreActions(store.shopName)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? menuId : undefined}
              onClick={() => (menuOpen ? closeMenu() : openMenu())}
            >
              <PexIcon name="more" size="sm" tone="strong" />
            </Button>
            {menuOpen && menuPos
              ? createPortal(
                  <div
                    ref={menuPanelRef}
                    id={menuId}
                    role="menu"
                    aria-label={`${store.shopName} actions`}
                    style={{
                      position: "fixed",
                      top: menuPos.top,
                      left: menuPos.left,
                      width: MENU_WIDTH,
                    }}
                    className="z-[100] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-1.5 shadow-[0_18px_44px_rgba(15,23,42,0.16)]"
                  >
                    {confirmDisconnect ? (
                      <div className="p-2">
                        <p className="text-sm font-semibold text-[var(--color-text)]">
                          {copy.actions.disconnectConfirmTitle}
                        </p>
                        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                          {copy.actions.disconnectConfirmBody}
                        </p>
                        <div className="mt-3 flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDisconnect(false)}
                          >
                            {copy.actions.cancel}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={disconnectPending}
                            onClick={() => {
                              closeMenu();
                              onDisconnect?.();
                            }}
                          >
                            {copy.actions.disconnectConfirmAction}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => runMenuAction(onViewStore)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:bg-[var(--color-surface-muted)] focus-visible:outline-none"
                        >
                          <PexIcon name="store" size="xs" tone="muted" />
                          {copy.actions.viewStore}
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => runMenuAction(onEditStore)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:bg-[var(--color-surface-muted)] focus-visible:outline-none"
                        >
                          <PexIcon name="edit" size="xs" tone="muted" />
                          {copy.actions.editStoreDetails}
                        </button>
                        {onReconnect ? (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => runMenuAction(onReconnect)}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:bg-[var(--color-surface-muted)] focus-visible:outline-none"
                          >
                            <PexIcon name="refresh" size="xs" tone="muted" />
                            {copy.actions.reconnect}
                          </button>
                        ) : null}
                        {onDisconnect ? (
                          <>
                            <div
                              role="separator"
                              className="my-1 h-px bg-[var(--color-border)]"
                            />
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => setConfirmDisconnect(true)}
                              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-700 transition-colors hover:bg-red-50 focus-visible:bg-red-50 focus-visible:outline-none"
                            >
                              <PexIcon name="disconnect" size="xs" tone="inherit" />
                              {copy.actions.disconnect}
                            </button>
                          </>
                        ) : null}
                      </>
                    )}
                  </div>,
                  document.body,
                )
              : null}
          </div>
        </div>
      </div>
      {detailsAvailable && detailsExpanded ? (
        <div
          id={detailsId}
          className="border-t border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3.5 py-3 sm:px-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[var(--color-text)]">
                {copy.group.orderIntake}
              </p>
              <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                {copy.group.orderIntakeDescription}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={store.receivingOrders}
              aria-label={
                store.receivingOrders
                  ? copy.actions.disableOrderIntake(store.shopName)
                  : copy.actions.enableOrderIntake(store.shopName)
              }
              disabled={!store.connected || orderIntakePending}
              onClick={onToggleOrderIntake}
              data-state={
                orderIntakeArming && !store.receivingOrders
                  ? "arming"
                  : undefined
              }
              aria-describedby={
                orderIntakeArming && !store.receivingOrders
                  ? detailsId
                  : undefined
              }
              className={`inline-flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] px-2 text-sm font-semibold transition-colors hover:bg-[var(--color-surface)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[36px] ${
                orderIntakeArming && !store.receivingOrders
                  ? "text-amber-700"
                  : "text-[var(--color-text)]"
              }`}
            >
              <span
                aria-hidden="true"
                className={`relative h-6 w-11 rounded-full transition-colors ${
                  store.receivingOrders
                    ? "bg-emerald-500"
                    : orderIntakeArming
                      ? "bg-amber-400"
                      : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${
                    store.receivingOrders
                      ? "translate-x-[22px]"
                      : orderIntakeArming
                        ? "translate-x-[12px]"
                        : "translate-x-0.5"
                  }`}
                />
              </span>
              {orderIntakePending
                ? copy.group.updating
                : store.receivingOrders
                  ? copy.group.on
                  : orderIntakeArming
                    ? copy.group.arming
                    : copy.group.off}
            </button>
          </div>
          {children ? (
            <div className="mt-3 border-t border-[var(--color-border)] pt-3">
              {children}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
